import { useState } from 'react'
import { BellOff, ChevronRight, Clock, Plus } from 'lucide-react'
import { clsx } from 'clsx'
import { useTopics, useCreateTopic, useChat, usePrefetchChat, type Topic } from '../lib/api'
import { relativeTimeFromNow, shortDateTimeLabel } from '../lib/time'
import { topicDisplayTitle } from '../lib/topicTitles'
import NewTopicPromptDialog from './NewTopicPromptDialog'

export default function TopicTabs({
  chatId,
  activeTopicId,
  onSelect,
}: {
  chatId: string
  activeTopicId: string | null
  onSelect: (id: string | null) => void
}) {
  const { data: chat } = useChat(chatId)
  const { data: topics = [] } = useTopics(chatId)
  const createTopic = useCreateTopic(chatId)
  const prefetchChat = usePrefetchChat()
  const [adding, setAdding] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [snoozedOpen, setSnoozedOpen] = useState(false)

  const visible = topics.filter((t) => !t.is_snoozed && !t.closed)
  const snoozed = topics
    .filter((t) => t.is_snoozed && !t.closed)
    .sort((a, b) => (a.snoozed_until || 0) - (b.snoozed_until || 0))

  // Topics only exist in groups (auto-spawn chats). Manual lanes never get
  // a topic UI at all — render nothing.
  if (chat?.type !== 'auto-spawn') return null

  // The "+ new" pill is only allowed at the group level. Once the user has
  // drilled into a specific topic, we hide the create-pill (topics can't
  // nest under topics).
  const canCreate = activeTopicId === null

  async function commitNew(openingPrompt: string) {
    setCreateError(null)
    try {
      const created = await createTopic.mutateAsync({ opening_prompt: openingPrompt })
      setAdding(false)
      onSelect(created.id)
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : 'Topic creation failed')
    }
  }

  if (topics.length === 0 && !adding) {
    if (!canCreate) return null
    return (
      <>
        <div className="px-3 py-1 border-b border-stone-200 dark:border-stone-800 flex items-center gap-2">
          <button
            onClick={() => {
              setCreateError(null)
              setAdding(true)
            }}
            className="text-xs flex items-center gap-1 text-stone-500 hover:text-stone-900 dark:hover:text-stone-100"
          >
            <Plus className="w-3 h-3" /> Add topic
          </button>
        </div>
        <NewTopicPromptDialog
          open={adding}
          pending={createTopic.isPending}
          error={createError}
          onCancel={() => {
            if (!createTopic.isPending) setAdding(false)
          }}
          onSubmit={commitNew}
        />
      </>
    )
  }

  return (
    <div className="relative z-20 border-b border-stone-200 dark:border-stone-800">
      <div className="overflow-x-auto px-3 py-1.5">
        <div className="flex items-center gap-1.5 min-w-max">
          <TopicChip
            label="All"
            active={activeTopicId === null}
            onClick={() => onSelect(null)}
            onWarm={() => prefetchChat(chatId, null)}
          />
          {visible.map((t) => (
            <TopicChip
              key={t.id}
              label={topicDisplayTitle(t)}
              active={activeTopicId === t.id}
              onClick={() => onSelect(t.id)}
              onWarm={() => prefetchChat(chatId, t.id)}
            />
          ))}
          {canCreate && (
            <button
              onClick={() => {
                setCreateError(null)
                setSnoozedOpen(false)
                setAdding(true)
              }}
              className="text-xs flex items-center gap-1 px-2 py-1 rounded-full text-stone-500 hover:bg-stone-100 dark:hover:bg-stone-800"
            >
              <Plus className="w-3 h-3" /> new
            </button>
          )}
          {snoozed.length > 0 && (
            <SnoozedTopicsButton
              count={snoozed.length}
              open={snoozedOpen}
              onToggle={() => setSnoozedOpen((v) => !v)}
            />
          )}
        </div>
      </div>
      {snoozedOpen && (
        <SnoozedTopicsMenu
          topics={snoozed}
          onClose={() => setSnoozedOpen(false)}
          onSelect={(topicId) => {
            setSnoozedOpen(false)
            onSelect(topicId)
          }}
          onWarm={(topicId) => prefetchChat(chatId, topicId)}
        />
      )}
      <NewTopicPromptDialog
        open={adding}
        pending={createTopic.isPending}
        error={createError}
        onCancel={() => {
          if (!createTopic.isPending) setAdding(false)
        }}
        onSubmit={commitNew}
      />
    </div>
  )
}

function TopicChip({
  label, active, onClick, onWarm,
}: { label: string; active: boolean; onClick: () => void; onWarm?: () => void }) {
  return (
    <button
      onClick={onClick}
      onPointerDown={onWarm}
      onTouchStart={onWarm}
      onMouseEnter={onWarm}
      onFocus={onWarm}
      className={clsx(
        'text-xs px-3 py-1 rounded-full transition-colors',
        active
          ? 'bg-blue-600 text-white'
          : 'bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 hover:bg-stone-200 dark:hover:bg-stone-700',
      )}
    >
      {label}
    </button>
  )
}

function SnoozedTopicsButton({
  count, open, onToggle,
}: { count: number; open: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-label={`${count} snoozed topics`}
      className={clsx(
        'text-xs flex items-center gap-1 px-2 py-1 rounded-full transition-colors',
        open
          ? 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100'
          : 'text-amber-700 hover:bg-amber-100/50 dark:text-amber-400 dark:hover:bg-amber-900/30',
      )}
    >
      <BellOff className="w-3 h-3" />
      {count} snoozed
    </button>
  )
}

function SnoozedTopicsMenu({
  topics, onClose, onSelect, onWarm,
}: {
  topics: Topic[]
  onClose: () => void
  onSelect: (topicId: string) => void
  onWarm: (topicId: string) => void
}) {
  return (
    <>
      <div className="fixed inset-0 z-20" onClick={onClose} />
      <div
        data-testid="snoozed-topic-menu"
        className="absolute right-3 top-full z-30 mt-1 w-[min(22rem,calc(100vw-1.5rem))] overflow-hidden rounded-lg border border-amber-200 bg-white shadow-2xl dark:border-amber-700/60 dark:bg-stone-900"
      >
        <div className="flex items-center gap-1.5 border-b border-amber-100 px-3 py-2 text-[10px] font-semibold uppercase text-amber-700 dark:border-amber-800/60 dark:text-amber-300">
          <Clock className="h-3 w-3" />
          Snoozed topics
        </div>
        <ul className="max-h-[min(28rem,70vh)] overflow-y-auto p-1">
          {topics.map((topic) => (
            <li key={topic.id}>
              <button
                type="button"
                data-testid="snoozed-topic-menu-item"
                onClick={() => onSelect(topic.id)}
                onPointerDown={() => onWarm(topic.id)}
                onTouchStart={() => onWarm(topic.id)}
                onMouseEnter={() => onWarm(topic.id)}
                onFocus={() => onWarm(topic.id)}
                className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-2 rounded-lg px-3 py-2 text-left transition-colors hover:bg-amber-50 dark:hover:bg-amber-950/40"
              >
                <span className="min-w-0">
                  <span className="block text-xs font-semibold leading-snug text-stone-800 dark:text-stone-100">
                    {topicDisplayTitle(topic)}
                  </span>
                  {topic.snoozed_until && (
                    <span className="mt-1 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] leading-tight text-amber-700 dark:text-amber-300">
                      <Clock className="h-3 w-3 shrink-0" aria-hidden />
                      <span className="whitespace-nowrap font-medium">
                        {relativeTimeFromNow(topic.snoozed_until)}
                      </span>
                      <span className="text-amber-500/80 dark:text-amber-400/80" aria-hidden>
                        &middot;
                      </span>
                      <span className="min-w-0 truncate">
                        {shortDateTimeLabel(topic.snoozed_until)}
                      </span>
                    </span>
                  )}
                </span>
                <ChevronRight className="h-4 w-4 text-stone-400" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      </div>
    </>
  )
}
